import { useEffect, useState } from "react";
import * as Menu from "@radix-ui/react-dropdown-menu";
import {
  ArrowRightLeft,
  ExternalLink,
  Film,
  Trash2,
  Upload,
} from "lucide-react";
import { DataTableCell, DataTableRow } from "@/components/ui/DataTable";
import type { Match } from "@/lib/types";
import {
  layoutLabel,
  matchDate,
  matchHref,
  matchStatusLabel,
  recordingLabel,
  recordingStatus,
} from "../matchLibrary";

export type MatchRowActions = {
  onDetails: () => void;
  onMedia: () => void;
  onEdit: () => void;
  onUpload: () => void;
  onDelete: () => void;
  onMove?: () => void;
};
export function MatchLibraryRow({
  match,
  teamName,
  seasonName,
  ...actions
}: MatchRowActions & { match: Match; teamName: string; seasonName: string }) {
  const [failedThumb, setFailedThumb] = useState(false);
  const status = recordingStatus(match);
  return (
    <DataTableRow
      data-testid="admin-matches-row"
      data-match-id={match.id}
      data-aggregate-status={status}
      className="admin-library-row"
      onClick={(event) => {
        if (
          (event.target instanceof Element &&
            event.target.closest('button,a,input,[role="menuitem"]')) ||
          window.getSelection()?.toString()
        )
          return;
        actions.onDetails();
      }}
    >
      <DataTableCell>
        <div className="admin-library-main">
          <span className="admin-library-thumb">
            {match.has_thumbnail && !failedThumb ? (
              <img
                src={`/api/matches/${encodeURIComponent(match.id)}/thumbnail`}
                alt=""
                loading="lazy"
                onError={() => setFailedThumb(true)}
              />
            ) : (
              <Film size={22} aria-hidden="true" />
            )}
          </span>
          <div className="admin-library-identity">
            <button
              type="button"
              data-testid="admin-matches-row-details"
              className="admin-library-title"
              onClick={actions.onDetails}
            >
              <strong>
                {match.home_team} vs {match.away_team}
              </strong>
            </button>
            <small
              data-testid="admin-matches-row-team"
              data-team-id={match.team_id}
            >
              {teamName}
            </small>
          </div>
        </div>
      </DataTableCell>
      <DataTableCell>
        <span className="admin-library-mobile-label">Date & season</span>
        <div className="admin-library-cell">
          <span>{matchDate(match)}</span>
          <small data-testid="admin-matches-row-season">{seasonName}</small>
        </div>
      </DataTableCell>
      <DataTableCell>
        <span className="admin-library-mobile-label">Competition</span>
        <div className="admin-library-cell">
          <span>
            {match.competition === "tournament"
              ? match.tournament_name || "Tournament"
              : match.competition === "friendly"
                ? "Friendly"
                : "League"}
          </span>
          <small>{match.stage || "—"}</small>
        </div>
      </DataTableCell>
      <DataTableCell className="admin-library-result-column">
        <span className="admin-library-mobile-label">Result</span>
        <div className="admin-library-cell admin-library-result">
          <strong className="admin-library-score">
            {match.score_home == null || match.score_away == null
              ? "—"
              : `${match.score_home}–${match.score_away}`}
          </strong>
          <small>{matchStatusLabel(match)}</small>
          {match.pk_home != null && match.pk_away != null ? (
            <small
              className="admin-library-pens"
              aria-label={`Penalty shootout ${match.pk_home} to ${match.pk_away}`}
            >
              Pens {match.pk_home}–{match.pk_away}
            </small>
          ) : null}
        </div>
      </DataTableCell>
      <DataTableCell>
        <span className="admin-library-mobile-label">Recording</span>
        <div className="admin-library-cell">
          <span
            className={`admin-match-video-badge admin-match-video-${status}`}
          >
            Video · {recordingLabel(match)}
          </span>
          <small>{layoutLabel(match)}</small>
        </div>
      </DataTableCell>
      <DataTableCell>
        <div className="admin-library-actions">
          <button
            type="button"
            className="admin-library-media"
            data-testid="admin-matches-row-expand"
            onClick={actions.onMedia}
          >
            Media slots
          </button>
          <button
            type="button"
            data-testid="admin-matches-row-edit"
            onClick={actions.onEdit}
          >
            Edit
          </button>
          <MatchLibraryMenu match={match} {...actions} />
        </div>
      </DataTableCell>
    </DataTableRow>
  );
}

function MatchLibraryMenu({
  match,
  onUpload,
  onDelete,
  onMove,
}: MatchRowActions & { match: Match }) {
  const [open, setOpen] = useState(false);
  // A replaced row must not leave actions operating on its old snapshot.
  useEffect(() => setOpen(false), [match.updated_at]);
  return (
    <Menu.Root modal={false} open={open} onOpenChange={setOpen}>
      <Menu.Trigger asChild>
        <button
          type="button"
          data-testid="admin-matches-row-more"
          aria-label={`More options for ${match.home_team} vs ${match.away_team}`}
        >
          More
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          className="admin-library-more-menu"
          sideOffset={6}
          align="end"
          collisionPadding={12}
          updatePositionStrategy="always"
          onClick={(event) => event.stopPropagation()}
        >
          <Menu.Item asChild>
            <a
              href={matchHref(match)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-matches-row-open"
            >
              <ExternalLink size={17} />
              Open match
            </a>
          </Menu.Item>
          <Menu.Item onSelect={onUpload} data-testid="admin-matches-row-upload">
            <Upload size={17} />
            Upload video
          </Menu.Item>
          {onMove ? (
            <Menu.Item onSelect={onMove} data-testid="admin-matches-row-move">
              <ArrowRightLeft size={17} />
              Move to squad
            </Menu.Item>
          ) : null}
          <Menu.Item
            onSelect={onDelete}
            className="admin-library-menu-danger"
            data-testid="admin-matches-row-delete"
          >
            <Trash2 size={17} />
            Delete match
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
